import React, { useState, useRef, useEffect } from "react";
import { FaSearch, FaTimes } from "react-icons/fa";

const SearchableDropdown = ({ question, value, onChange }) => {
  const { questionId, options = [], placeholder = "Search..." } = question;
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Find the selected option's label
  const selectedOption = options.find((o) => o.value === value);

  // Filter options by query
  const filtered = query.trim()
    ? options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()))
    : options;

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (option) => {
    onChange(questionId, option.value);
    setQuery("");
    setIsOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange(questionId, null);
    setQuery("");
  };

  return (
    <div ref={dropdownRef} className="relative">
      {/* Selected value display / search input */}
      <div
        className={`flex items-center gap-2 px-4 py-3 rounded-lg border cursor-pointer transition-all ${
          isOpen
            ? "border-spiders-primary-500 ring-1 ring-spiders-primary-500/30 bg-spiders-bg-surface"
            : value
            ? "border-spiders-primary-500/30 bg-spiders-primary-500/5"
            : "border-spiders-border bg-spiders-bg-elevated hover:bg-spiders-bg-light"
        }`}
        onClick={() => {
          setIsOpen(true);
          setTimeout(() => inputRef.current?.focus(), 50);
        }}
      >
        <FaSearch className="w-3.5 h-3.5 text-spiders-text-muted flex-shrink-0" />
        {isOpen ? (
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={selectedOption ? selectedOption.label : placeholder}
            className="flex-1 bg-transparent text-sm text-spiders-text-primary outline-none placeholder-spiders-text-muted"
            autoFocus
          />
        ) : (
          <span className={`flex-1 text-sm ${value ? "text-spiders-text-primary" : "text-spiders-text-muted"}`}>
            {selectedOption ? selectedOption.label : placeholder}
          </span>
        )}
        {value && (
          <button
            onClick={handleClear}
            className="text-spiders-text-muted hover:text-spiders-text-primary p-0.5"
          >
            <FaTimes className="w-3 h-3" />
          </button>
        )}
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-spiders-bg-surface border border-spiders-border rounded-lg shadow-xl z-50 max-h-48 overflow-y-auto">
          {filtered.length > 0 ? (
            filtered.map((option) => (
              <button
                key={option.value}
                onClick={() => handleSelect(option)}
                className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                  value === option.value
                    ? "bg-spiders-primary-500/10 text-spiders-primary-400 font-medium"
                    : "text-spiders-text-secondary hover:bg-spiders-bg-elevated"
                }`}
              >
                {option.label}
              </button>
            ))
          ) : (
            <div className="px-4 py-3 text-sm text-spiders-text-muted">
              No results for "{query}"
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchableDropdown;
